class ShopifyInfiniteScroll {
  constructor() {
    this.loading = false;
    this.finished = false;
    this.loadedUrls = new Set();

    this.productGrid =
      document.querySelector('#product-grid') ||
      document.querySelector('[data-product-grid]') ||
      document.querySelector('.product-grid') ||
      document.querySelector('.collection-products-grid');

    this.pagination =
      document.querySelector('.pagination-wrapper') ||
      document.querySelector('.pagination');

    if (!this.productGrid || !this.pagination) {
      console.warn(
        'Infinite scroll: Ürün alanı veya pagination bulunamadı.'
      );
      return;
    }

    /*
     * Sonraki URL'yi yalnızca başlangıçta pagination içinden alıyoruz.
     * Daha sonraki sayfalarda URL doğrudan indirilen belgeden okunacak.
     */
    this.nextPageUrl = this.findNextPageUrl(
      document,
      window.location.href
    );

    this.createLoader();

    if (!this.nextPageUrl) {
      this.finish();
      return;
    }

    this.observeLoader();
  }

  getPaginationRoot(root) {
    return (
      root.querySelector('.pagination-wrapper') ||
      root.querySelector('.pagination')
    );
  }

  findNextPageUrl(root, currentPageUrl) {
    const pagination = this.getPaginationRoot(root);

    if (!pagination) {
      return null;
    }

    /*
     * Önce Shopify'ın standart next bağlantılarını kontrol et.
     */
    const directNextLink =
      pagination.querySelector('a[rel="next"]') ||
      pagination.querySelector('.next a') ||
      pagination.querySelector('a.pagination__item--next') ||
      pagination.querySelector('a.pagination__next') ||
      pagination.querySelector('a.next') ||
      pagination.querySelector(
        'a[aria-label*="Next" i]'
      ) ||
      pagination.querySelector(
        'a[aria-label*="Sonraki" i]'
      );

    if (directNextLink?.getAttribute('href')) {
      return new URL(
        directNextLink.getAttribute('href'),
        window.location.origin
      ).href;
    }

    /*
     * Iris temasının standart next sınıfı yoksa bütün sayfa
     * bağlantılarını okuyup mevcut sayfadan sonraki en küçük
     * sayfa numarasını bulur.
     */
    const currentUrl = new URL(
      currentPageUrl,
      window.location.origin
    );

    const paginationLinks = Array.from(
      pagination.querySelectorAll('a[href]')
    );

    const candidates = [];

    paginationLinks.forEach(link => {
      const href = link.getAttribute('href');

      if (!href) return;

      const linkUrl = new URL(href, window.location.origin);

      linkUrl.searchParams.forEach((value, key) => {
        /*
         * Shopify normalde page kullanır. Bazı listelerde
         * page_a123 gibi benzersiz parametreler oluşturabilir.
         */
        if (!key.startsWith('page')) return;

        const pageNumber = parseInt(value, 10);

        if (!Number.isInteger(pageNumber)) return;

        const currentPageNumber = parseInt(
          currentUrl.searchParams.get(key) || '1',
          10
        );

        if (pageNumber > currentPageNumber) {
          candidates.push({
            pageNumber,
            url: linkUrl.href
          });
        }
      });
    });

    if (candidates.length === 0) {
      return null;
    }

    /*
     * Mevcut sayfadan sonraki en yakın sayfayı seç.
     * Örneğin 2. sayfadaysak 3. sayfayı seçer.
     */
    candidates.sort(
      (first, second) =>
        first.pageNumber - second.pageNumber
    );

    return candidates[0].url;
  }

  createLoader() {
    this.loader = document.createElement('div');
    this.loader.className = 'infinite-scroll-loader';

    this.showLoading();

    this.pagination.insertAdjacentElement(
      'afterend',
      this.loader
    );

    /*
     * Pagination HTML içinde kalır.
     * Bu, JavaScript çalışmazsa sayfalama bağlantılarının
     * hâlâ mevcut olmasını sağlar.
     */
    this.pagination.style.display = 'none';
  }

  showLoading() {
    this.loader.innerHTML = `
      <span class="infinite-scroll-spinner"></span>
      <span>Ürünler yükleniyor...</span>
    `;
  }

  observeLoader() {
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          this.loadNextPage();
        }
      },
      {
        rootMargin: '600px 0px',
        threshold: 0
      }
    );

    this.observer.observe(this.loader);
  }

  getProductItems(grid) {
    /*
     * Önce doğrudan alt elemanları al.
     * Iris ürün kartlarının tamamı burada bulunmalıdır.
     */
    const directChildren = Array.from(grid.children).filter(
      element =>
        element.matches(
          'li, .grid__item, [data-product-id], .product-item, .product-card'
        )
    );

    /*
     * Tema sınıfları yukarıdakilerden farklıysa bütün doğrudan
     * alt elemanları ürün olarak kullan.
     */
    return directChildren.length
      ? directChildren
      : Array.from(grid.children);
  }

  async loadNextPage() {
    if (
      this.loading ||
      this.finished ||
      !this.nextPageUrl
    ) {
      return;
    }

    const pageUrl = this.nextPageUrl;

    /*
     * Aynı sayfanın ikinci defa yüklenmesini engelle.
     */
    if (this.loadedUrls.has(pageUrl)) {
      console.warn(
        'Infinite scroll aynı sayfayı tekrar yüklemeye çalıştı:',
        pageUrl
      );

      this.finish();
      return;
    }

    this.loading = true;
    this.loadedUrls.add(pageUrl);
    this.showLoading();

    try {
      const response = await fetch(pageUrl, {
        method: 'GET',
        headers: {
          Accept: 'text/html'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(
          `Sayfa yüklenemedi: ${response.status}`
        );
      }

      const html = await response.text();

      const parser = new DOMParser();
      const nextDocument = parser.parseFromString(
        html,
        'text/html'
      );

      const nextGrid =
        nextDocument.querySelector('#product-grid') ||
        nextDocument.querySelector('[data-product-grid]') ||
        nextDocument.querySelector('.product-grid') ||
        nextDocument.querySelector(
          '.collection-products-grid'
        );

      if (!nextGrid) {
        console.warn(
          'Sonraki sayfada ürün grid alanı bulunamadı:',
          pageUrl
        );

        this.finish();
        return;
      }

      const nextProducts = this.getProductItems(nextGrid);

      if (nextProducts.length === 0) {
        console.warn(
          'Sonraki sayfada ürün kartı bulunamadı:',
          pageUrl
        );

        this.finish();
        return;
      }

      const fragment = document.createDocumentFragment();

      nextProducts.forEach(product => {
        fragment.appendChild(product);
      });

      this.productGrid.appendChild(fragment);

      /*
       * En önemli düzeltme:
       * Sonraki URL, eski pagination alanından değil,
       * yeni indirilen sayfanın pagination alanından bulunur.
       */
      this.nextPageUrl = this.findNextPageUrl(
        nextDocument,
        pageUrl
      );

      console.log(
        `${nextProducts.length} ürün eklendi.`,
        'Sonraki sayfa:',
        this.nextPageUrl || 'Son sayfa'
      );

      document.dispatchEvent(
        new CustomEvent('shopify:products-loaded', {
          detail: {
            loadedUrl: pageUrl,
            nextPageUrl: this.nextPageUrl,
            addedProducts: nextProducts.length
          }
        })
      );

      if (!this.nextPageUrl) {
        this.finish();
      }
    } catch (error) {
      console.error('Infinite scroll hatası:', error);

      this.loadedUrls.delete(pageUrl);

      this.loader.innerHTML = `
        <button
          type="button"
          class="infinite-scroll-retry"
        >
          Ürünleri tekrar yükle
        </button>
      `;

      const retryButton = this.loader.querySelector(
        '.infinite-scroll-retry'
      );

      retryButton?.addEventListener(
        'click',
        () => {
          this.loadNextPage();
        },
        { once: true }
      );
    } finally {
      this.loading = false;
    }
  }

  finish() {
    if (this.finished) return;

    this.finished = true;
    this.loading = false;
    this.nextPageUrl = null;

    this.observer?.disconnect();

    if (this.loader) {
      this.loader.innerHTML = `
        <span class="infinite-scroll-finished">
          Tüm ürünleri görüntülediniz.
        </span>
      `;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ShopifyInfiniteScroll();
});